import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, SimpleChanges } from "@angular/core";
import { CustomizerColorsAutoUpdateComponent, CustomizerComponentHeaderComponent, CustomizerListComponent } from '@app/customizer/components';
import { ComponentVariantFromApiModel } from "@models/api-models/component-variant-fromapi";
import { ComponentVariantModel } from '@models/component-variant';
import { ComponentVariantChangeModel } from '@models/component-variant-change';
import { VariantColorModel } from "@models/variant-color";
import { VariantSizeModel } from "@models/variant-size";
import { TranslatePipe } from '@ngx-translate/core';
import { ConfigurationService } from '@services/configuration.service';
import { DataApiService } from '@services/data-api.service';
import { EventsService } from '@services/events.service';
import { ImageService } from "@services/image.service";
import { SorterService } from "@services/sorter.service";
import { ComponentCodeConstants } from "@shared/component-constants";
import { AngleConstants } from "../../../../shared/angle-constants";
import { BaseCustomizerComponent } from "../../../../shared/base-customizer-component";

@Component({
	styleUrls: ['./gemstone.scss'],
	templateUrl: 'gemstone.html',
	changeDetection: ChangeDetectionStrategy.Eager,
	imports: [CustomizerComponentHeaderComponent, TranslatePipe, CommonModule, CustomizerListComponent, CustomizerColorsAutoUpdateComponent]
})
export class GemstoneComponent extends BaseCustomizerComponent {

	public sizes: Array<VariantSizeModel> = [];
	public selectedSizeIndex: number = -1;
	public selectedColor: VariantColorModel | null = null;
	public showInfo: boolean;
	public showNoneChoiceOption: boolean = false;
	public showGemstoneOptions: boolean = false;
	public noneChoiceTranslation: string;
	public selectableVariants: Array<ComponentVariantFromApiModel> = [];
	public availableOnVariants: string | null = null;
	public notAvailableOnColors: string | null = null;
	public variantCode: string | null = null;
	public noGemstoneImage: string | null = null;

	constructor(
		public override events: EventsService,
		public apiService: DataApiService,
		private sorter: SorterService,
		private imageService: ImageService,
		public override configurationService: ConfigurationService
	) {
		super(events, apiService, configurationService);
	}

	onChanges(changes: SimpleChanges): void {
		this.setSelectedSizeIndex(this.sizes);
	}

	async initData() {
		this.events.showLoader(true);

		this.setImageSliderByName(AngleConstants.Perspective);

		this.sizes = await this.fetchSizeData() ?? [];
		this.selectableVariants = await this.fetchColorData() ?? [];

		const gemstoneVariant = this.variants.filter(v => v.Component.Code == ComponentCodeConstants.Gemstone)[0];
		let chosenSizeIndex = -1; //no variant
		const isDefault = gemstoneVariant?.Default;

		if (gemstoneVariant.Optional && isDefault) {
			chosenSizeIndex = this.sizes.findIndex(s => s.Id === gemstoneVariant.VariantSize?.Id);
		}
		else {
			chosenSizeIndex = -1; //choose none

			if (isDefault) {
				chosenSizeIndex = this.sizes.findIndex(s => s.Id === gemstoneVariant.VariantSize?.Id);
			}
		}

		this.onSelect(chosenSizeIndex, null);

		this.noneChoiceTranslation = `CUSTOMIZER.COMPONENTS.GEMSTONE.NOGEMSTONE`;

		this.noGemstoneImage = this.imageService.GetNoGemstoneImage(this.modelGroup?.ProductLineCode);

		this.events.showLoader(false);
	}

	toggleGemstoneOptions() {
		this.onSelect(-1, null)
	}

	setSelectedSizeIndex(sizes: Array<VariantSizeModel>) {
		if (this.selectedSizeIndex === -1) {
			return;
		}

		if (this.getSelectedVariant.VariantSize) {
			this.selectedSizeIndex = sizes.findIndex(x => x?.Uuid == this.getSelectedVariant.VariantSize?.Uuid);
		}
		else {
			this.selectedSizeIndex = 0;
		}
	}

	onSelect(index: number, color: VariantColorModel | null) {
		const newVariant = Object.assign(
			new ComponentVariantModel(this.getSelectedVariant.Discontinued, this.getSelectedVariant.New),
			this.getSelectedVariant
		);

		const oldVariant = Object.assign(
			new ComponentVariantModel(this.getSelectedVariant.Discontinued, this.getSelectedVariant.New),
			this.getSelectedVariant
		);

		let doSizeUpdate = this.selectedSizeIndex !== index;
		let doColorUpdate = this.selectedColor !== color;

		this.events.showLoader(true);

		this.selectedSizeIndex = index;
		let size = this.sizes[index];
		newVariant.VariantSize = size;
		newVariant.NotSelected = size == null;

		if (doSizeUpdate && !doColorUpdate) {
			this.updateComponentSize(new ComponentVariantChangeModel(oldVariant, newVariant, null));

			setTimeout(() => {
				if (newVariant.VariantColor) {
					color = newVariant.VariantColor;
					this.updateColor(oldVariant, newVariant, color)
				}
			}, 50);
		}
		else {
			this.updateColor(oldVariant, newVariant, color)
		}
	}

	updateColor(oldVariant: ComponentVariantModel, newVariant: ComponentVariantModel, color: VariantColorModel | null) {
		newVariant.VariantColor = color;
		newVariant.NotSelected = color == null;

		this.selectedColor = color;

		setTimeout(() => {
			this.updateVariant(new ComponentVariantChangeModel(oldVariant, newVariant, null));
		}, 50);
	}

	async fetchSizeData(): Promise<Array<VariantSizeModel> | undefined> {
		const variant = this.getSelectedVariant;
		variant.VariantSizeViewed = true;

		const apidata = await this.configurationService.getVariants(this.configuration?.ModelConfigurationUuid, this.modelGroup, undefined, variant.Uuid);
		await this.RunDisablingSystem(apidata);

		this.variantCode = apidata?.[0].Code ?? null;

		const variantSizes = apidata?.[0].VariantSizes.filter(x => !x.isDisabled).sort(this.sorter.sortBy(VariantSizeModel));
		variantSizes?.forEach(variantSize => {
			variantSize.ComponentCode = ComponentCodeConstants.Gemstone;
		});

		return variantSizes;
	}

	async fetchColorData(): Promise<Array<ComponentVariantFromApiModel> | undefined> {
		this.availableOnVariants = null;
		this.notAvailableOnColors = null;

		const variant = this.getSelectedVariant;
		variant.VariantColorViewed = true;

		const apidata = await this.configurationService.getVariants(
			this.configuration?.ModelConfigurationUuid,
			this.modelGroup,
			undefined,
			variant.Uuid,
			variant.VariantSize?.Uuid
		);

		await this.RunDisablingSystem(apidata);

		return apidata?.filter(x => !x.isDisabled);
	}

	getImage = (variantSizeModel: VariantSizeModel) => {
		return this.imageService.GetComponentDetailPreciousImage(this.getSelectedVariant.Component.Code, this.variantCode, variantSizeModel, this.modelGroup.ProductLineCode);
	}

}
